import { useCallback, useEffect, useState } from 'react'
import useSWR, { SWRResponse } from 'swr'
import dayjs, { Dayjs } from 'dayjs'
import DownloadIcon from '@mui/icons-material/Download'
import { Grid, IconButton, Tooltip, Typography } from '@mui/material'
import { useParams, useSearchParams } from 'react-router-dom'
import Loading from '../../../components/Loading'
import BoxWrapper from '../../../components/BoxWrapper'
import TableRender from '../../../components/TableRender'
import axiosInstance from '../../../utils/axiosInstance'
import classes from './style.module.scss'
import { classifyLeadField, firstFieldValue, parseFieldData } from '../../../utils/leadFields'
import { leadStatuses } from '../../../constants'
import { dialogToggle } from '../../../store/slices/dialog'
import { setTrigger } from '../../../store/slices/lead'
import { useDispatch, useSelector } from '../../../store/store'

interface Lead {
  id: string
  status?: string
  createdAt: Dayjs | string
  firstName: string
  lastName: string
  phone: string
  email: string
  platform: string
  question: {
    name: string
    value: string
  }
}

interface Info {
  question: {
    name: string
    value: string
  }
  firstName: string
  lastName: string
  email: string
  phone: string
}

function createData(
  id: string,
  createdAt: Dayjs | string,
  firstName: string,
  lastName: string,
  phone: string,
  email: string,
  platform: string,
  question: {
    name: string
    value: string
  },
  status?: string
): Lead {
  return {
    id,
    createdAt,
    firstName,
    lastName,
    phone,
    email,
    platform,
    question,
    status,
  }
}

// Map a form field to a standard applicant field; misspelled keys ("full name") still match.
const getFieldType = (fieldName: string): string => classifyLeadField(fieldName) || 'question'

// Helper function to normalize platform values
const normalizePlatform = (platformValue) => {
  if (!platformValue) return null

  // Handle common variations
  // Audience Network ('an') and Messenger ('msg') are Meta/Facebook surfaces,
  // so we group them under the Facebook platform for display.
  if (
    platformValue.toLowerCase() === 'fb' ||
    platformValue.toLowerCase() === 'an' ||
    platformValue.toLowerCase() === 'msg' ||
    platformValue.toLowerCase().includes('facebook')
  ) {
    return 'facebook'
  }
  if (platformValue.toLowerCase() === 'ig' || platformValue.toLowerCase().includes('instagram')) {
    return 'instagram'
  }
  if (
    platformValue.toLowerCase().includes('internal') ||
    platformValue.toLowerCase().includes('recruspin')
  ) {
    return 'internalRecruspin'
  }

  return platformValue.toLowerCase()
}

// ============================|| LEADS LIST COMPONENT ||============================ //

interface LeadsListProps {
  /**
   * Optionally override the campaign id that is normally taken from the URL parameter.
   * This is useful when the component is rendered inside another view (e.g. Order Tree tab)
   * where the surrounding route does not expose the campaign id as ":id" path param.
   */
  campaignId?: string
}

export const LeadsList = ({ campaignId }: LeadsListProps) => {
  // "id" comes from URL (/campaigns/:id) when the component is used stand-alone.
  const { id: idFromParams } = useParams<{ id: string }>()

  // Allow an explicit prop to override the URL param.
  const id = campaignId || idFromParams

  const [rows, setRows] = useState<unknown[] | never[]>()
  const [page, setPage] = useState(0)
  const defaultRowsPerPage = Number(localStorage.getItem('campaignTableRowsPerPage')) || 5
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage)

  // const { data: totalOrders } = useSWR(`/campaigns/${id}/total-count`, axiosInstance)
  // const totalCount = totalOrders?.data.data

  const take = page * rowsPerPage + rowsPerPage + 1
  const requestPath = `/campaign?take=${take}`
  const axiosCall = () => {
    return axiosInstance.get(requestPath, {
      params: { id },
    })
  }
  const [searchParams] = useSearchParams()
  const leadsId = searchParams.get('lead') || null
  const { trigger } = useSelector((state) => state.lead)
  const dispatch = useDispatch()

  const { data, isLoading, mutate }: SWRResponse = useSWR(requestPath, axiosCall, {
    revalidateIfStale: true,
    revalidateOnMount: true,
  })

  const statusCounter = (data, status) => ({
    [status]: data?.filter((item) => item.status === status).length,
  })

  const handleDownloadCB = useCallback(() => {
    const url = `/campaign/${id}/download`
    axiosInstance
      .get(url, {
        responseType: 'blob',
      })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        const fileName = `campaign_${id}.csv`
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
      })
      .catch((error) => {
        throw new Error(error)
      })
  }, [id])

  const StatsItem = ({ status: { label, value }, rows }) => {
    const getStatuses = statusCounter(rows, value)

    return (
      <Grid
        item
        xs="auto"
        container
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px',
          borderRadius: '8px',
          flex: '1 1 auto',
          border: '1px solid  #EEE',
        }}
      >
        <p className={`${classes.status} ${classes[value]}`}>{label}</p>
        <p
          className={`${classes.status} ${classes[value]}`}
          style={{ width: '20px', height: '20px' }}
        >
          {getStatuses[value]}
        </p>
      </Grid>
    )
  }

  useEffect(() => {
    if (trigger) {
      mutate()
      dispatch(setTrigger(false))
    }
  }, [dispatch, mutate, trigger])

  useEffect(() => {
    if (leadsId) {
      dispatch(dialogToggle(true))
    }
  }, [dispatch, leadsId])

  useEffect(() => {
    if (data?.data.campaignTitle) {
      const rowsData = data?.data?.leads.map((lead) => {
        const { id, createdTime: createdAt, fieldData, platform: rawPlatform, status } = lead
        const reformat: Info = {
          firstName: '',
          lastName: '',
          phone: '',
          email: '',
          question: { name: '', value: '' },
        }

        try {
          parseFieldData(fieldData).forEach((item) => {
            const fieldType = getFieldType(item.name)
            const value = firstFieldValue(item)

            switch (fieldType) {
              case 'full_name': {
                // Split name if possible, otherwise use full value as firstName
                const nameParts = value.split(' ')
                reformat.firstName = nameParts[0] || value
                reformat.lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : ''
                break
              }
              case 'first_name':
                if (!reformat.firstName) reformat.firstName = value
                break
              case 'last_name':
                if (!reformat.lastName) reformat.lastName = value
                break
              case 'email':
                reformat.email = reformat.email || value
                break
              case 'phone_number':
                reformat.phone = reformat.phone || value
                break
              default:
                reformat.question = {
                  name: item.name,
                  value: Array.isArray(item.values)
                    ? item.values.join(', ')
                    : String(item.values || ''),
                }
                break
            }
          })
        } catch (error) {
          console.error('Error parsing field data:', error)
        }

        const { firstName, lastName, phone, email, question } = reformat
        const platform = normalizePlatform(rawPlatform)

        return createData(
          id,
          dayjs(createdAt),
          firstName,
          lastName,
          phone,
          email,
          platform,
          question,
          status
        )
      })

      setRows(rowsData)
    }
  }, [data, dispatch])

  useEffect(() => {
    // Only a lead id opens the dialog; `?legacy=1` alone must not (see LegacyCampaignRedirect).
    const params = new URLSearchParams(window.location.search)
    if (params.has('id') || params.has('lead')) {
      dispatch(dialogToggle(true))
    }
  }, [dispatch])

  return (
    <BoxWrapper height="100%">
      <div className={classes.tableHeader}>
        <div className={classes.header}>
          {data?.data?.campaignTitle && (
            <div className={classes.header}>
              <div className={classes.titleContainer}>
                <Typography variant="h2">{data?.data?.campaignTitle}</Typography>
                <span className={classes.counter}>({data?.data?.leads?.length || 0})</span>
              </div>
              <Tooltip onClick={handleDownloadCB} title="Download CSV">
                <IconButton>
                  <DownloadIcon style={{ color: '#757575', height: '1em' }} />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
      <Grid container justifyContent="space-between" padding={'16px'} gap={2}>
        {isLoading ? (
          <Loading />
        ) : (
          leadStatuses.map((status) => <StatsItem key={status.value} status={status} rows={rows} />)
        )}
      </Grid>
      <TableRender
        data={rows}
        type={'lead'}
        defaultOrderBy={'createdAt'}
        isLoading={isLoading}
        toolbar
        pagination
        densePadding
        page={page}
        rowsPerPage={rowsPerPage}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        rowsPerPageItemName="campaignTableRowsPerPage"
      />
    </BoxWrapper>
  )
}
